import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, IMAGES, COVERS } from "@/lib/site";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [book, setBook] = useState(null);
  const [buying, setBuying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API}/blog/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((d) => setPost(d.post))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!post?.ebook_lookup_key) return;
    fetch(`${API}/ebooks`)
      .then((r) => r.json())
      .then((d) => {
        const all = [...(d.ebooks || []), ...(d.bundle ? [d.bundle] : [])];
        setBook(all.find((e) => e.lookup_key === post.ebook_lookup_key) || null);
      })
      .catch(() => {});
  }, [post]);

  const buyBook = async () => {
    if (!book) return;
    setBuying(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lookup_key: book.lookup_key, quantity: 1, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
      else throw new Error();
    } catch {
      toast.error("Checkout could not start. Please try again.");
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-brand-bg flex items-center justify-center">
        <Spinner size={40} className="animate-spin text-brand-gold" />
      </main>
    );
  }

  if (notFound) {
    return (
      <main className="min-h-screen bg-brand-bg flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="font-serif text-3xl text-brand-text">Post not found</h1>
          <Link to="/" className="text-brand-gold text-sm mt-4 inline-block">← Back to home</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-brand-bg" data-testid="blog-post-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-28 pb-24">
        <Link to="/#journal" className="text-sm text-brand-dim hover:text-brand-gold flex items-center gap-1 mb-10">
          <ArrowLeft size={14} /> Back to the journal
        </Link>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="flex items-center gap-3 mb-5">
            <span className="overline">{post.tag}</span>
            <span className="text-xs text-brand-dim">· {post.read}</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-brand-text leading-tight">
            {post.title}
          </h1>
          {post.excerpt && <p className="text-brand-dim text-lg mt-5">{post.excerpt}</p>}
          {post.image && (
            <div className="rounded-3xl overflow-hidden border border-brand-line mt-10">
              <img src={post.image} alt={post.title} className="w-full max-h-[460px] object-cover" />
            </div>
          )}
          <div className="mt-10 text-brand-dim text-lg leading-relaxed whitespace-pre-wrap">
            {post.body}
          </div>
        </motion.div>

        {book && (
          <motion.div
            data-testid="blog-book-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-14 rounded-3xl border border-brand-line bg-brand-surface p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6"
          >
            {COVERS[book.lookup_key] && (
              <img
                src={COVERS[book.lookup_key]}
                alt={book.title}
                className="w-28 h-40 object-cover rounded-xl border border-brand-line shrink-0 shadow-lg"
              />
            )}
            <div className="flex-1 text-center sm:text-left">
              <span className="overline">Read the full book</span>
              <h3 className="font-serif text-2xl text-brand-text mt-2">{book.title}</h3>
              {book.subtitle && <p className="text-sm text-brand-dim mt-1">{book.subtitle}</p>}
              <button
                data-testid="blog-book-buy"
                onClick={buyBook}
                disabled={buying}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-gold px-7 py-3 text-sm font-medium text-brand-bg hover:bg-brand-text transition-colors disabled:opacity-60"
              >
                {buying ? <Spinner size={16} className="animate-spin" /> : <>Read the full book · ${book.price.toFixed(0)} <ArrowRight size={16} /></>}
              </button>
            </div>
          </motion.div>
        )}

        <div className="mt-16 pt-8 border-t border-brand-line">
          <p className="font-serif italic text-2xl text-brand-gold">TonyBrand29</p>
          <p className="overline mt-1">Family Physician · Coach</p>
        </div>
      </div>
    </main>
  );
}
